import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import RecentActivityCard from '../../shared/RecentActivityCard';
import { fetchEntityAuditLog } from '../../../store/actions/AuditLogActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import apis from '../../../utils/apis';
import {
  PiArrowLeftLight,
  PiPencilLight,
  PiXLight,
  PiTrashLight,
  PiPlusCircleLight,
  PiSpinnerLight,
  PiTextAlignLeftLight,
  PiCircleDashedLight,
  PiUsersLight,
  PiCalendarBlankLight,
  PiClockClockwiseLight,
  PiClockCounterClockwiseLight,
  PiTagLight,
} from 'react-icons/pi';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import ProfileImage from '../../Header/ProfileImage';
import DetailPageSkeleton from '../Shared/DetailPageSkeleton';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { fetchPracticeAreas } from '../../../store/actions/DropdownActions';
import {
  fetchDepartmentDetails,
  updateDepartment,
  addDepartmentMember,
  removeDepartmentMember,
  fetchDepartmentFeed,
  postDepartmentFeedEvent,
  deleteDepartment,
} from '../../../store/actions/organization/DepartmentActions';
import MemberSelectionModal from '../Shared/MemberSelectionModal';
import FeedComponent from '../Shared/FeedComponent';
import DetailFrame from '../../shared/Frames/DetailFrame';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import DocumentsTab from './DocumentsTab';
import CollaboratorSelector from '../../shared/CollaboratorSelector';
import MembersSection from '../Shared/MembersSection';
import { formatDate, formatDateTime } from '../../../utils/functions';
import AuditLogPanel from '../../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, departmentDetailTourSteps } from '../../shared/FeatureTour';

interface Department {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  member_count: number;
  members: Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role?: string;
    joined_at?: string;
    profile_picture?: string | null;
  }>;
  created_at?: string;
  practice_area?: string;
  practice_areas?: Array<{ id: number; name: string }>;
  practice_area_ids?: number[];
}

interface DepartmentDetailParams {
  departmentId: string;
}

const DepartmentDetail: React.FC = () => {
  const { t } = useTranslation();
  const { departmentId } = useParams<DepartmentDetailParams>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const practiceAreas = useSelector((state: RootState) => state.dropdown.dropdowns.practiceAreas) || [];

  const [department, setDepartment] = useState<Department | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'Details' | 'feed' | 'matters' | 'documents'>('Details');
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [feed, setFeed] = useState<any[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [uploadingDocCount, setUploadingDocCount] = useState(0);
  const [isEditingDeptInfo, setIsEditingDeptInfo] = useState(false);
  const [editedDeptFields, setEditedDeptFields] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [collaborators, setCollaborators] = useState<any[]>([]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'department-detail',
    steps: departmentDetailTourSteps,
    readyToShow: !loading && !!department,
    autoStartDelay: 600,
  });

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  useEffect(() => {
    dispatch(fetchPracticeAreas());
  }, [dispatch]);

  const practiceAreaOptions = practiceAreas.map((area: any) => ({
    value: String(area.value || area.id),
    label: String(area.label || area.name),
  }));

  const loadDepartment = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await dispatch(fetchDepartmentDetails(departmentId) as any);
      if (result) {
        setDepartment(result);
        setEditedDeptFields(result);
      } else {
        setError(t('Failed to load department'));
      }
    } catch (err) {
      setError(t('Failed to load department'));
    } finally {
      setLoading(false);
    }
  }, [departmentId, dispatch]);

  const handleDeptFieldChange = (field: string, value: any) => {
    setEditedDeptFields((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSaveDepartment = async () => {
    if (!department?.id) return;
    setIsSaving(true);
    try {
      await dispatch(updateDepartment(department.id, {
        name: editedDeptFields.name,
        description: editedDeptFields.description,
        practice_area_ids: (editedDeptFields.practice_area_ids || []).map(Number),
      }));
      setIsEditingDeptInfo(false);
      await loadDepartment();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Department updated successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (err) {
      console.error('Error saving department:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to save department'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingDeptInfo(false);
    setEditedDeptFields(department || {});
  };

  const handleAddCollaborators = async () => {
    if (collaborators.length === 0) return;
    setIsSaving(true);
    try {
      for (const collab of collaborators) {
        const memberId = collab.collaborator_uuid || String(collab.user_id || '');
        if (!memberId) continue;
        await dispatch(addDepartmentMember(departmentId, memberId, collab.access === 'edit' ? 'member' : 'member'));
      }
      setCollaborators([]);
      await loadDepartment();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Members added successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (err) {
      console.error('Error adding collaborators:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to add members'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  };


  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const result = await dispatch(fetchDepartmentFeed(departmentId) as any);
      if (Array.isArray(result)) {
        setFeed(result.reverse());
      }
    } finally {
      setFeedLoading(false);
    }
  }, [departmentId, dispatch]);

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const response = await apis.get(`/org-people/departments/${departmentId}/documents`);
      setDocuments(Array.isArray(response.data.documents) ? response.data.documents : []);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    if (!departmentId) return;
    loadDepartment();
    loadFeed();
  }, [departmentId, loadDepartment, loadFeed]);

  useEffect(() => {
    if (activeTab === 'documents') {
      loadDocuments();
    }
    if (activeTab === 'feed') {
      loadFeed();
    }
  }, [activeTab, loadDocuments, loadFeed]);


  const handleAddMember = async (member: any) => {
    try {
      await dispatch(addDepartmentMember(departmentId, member.id, 'member'));
      await loadDepartment();
      dispatch(hideModal());
    } catch (err) {
      console.error('Error adding member:', err);
    }
  };

  const handleRemoveMember = (memberId: string) => {
    dispatch(showModal({
      type: 'confirmation',
      message: t('Remove Member'),
      subMsg: t('This member will be removed from the department and lose access to all department resources.'),
      icon:  <PiTrashLight className='w-6 h-6 text-red-500' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        try {
          await dispatch(removeDepartmentMember(departmentId, memberId));
          await loadDepartment();
        } catch (err) {
          console.error('Error removing member:', err);
        }
        dispatch(hideModal())
      },
    }));
  };

  const handleSetLead = async (memberId: string) => {
    try {
      await apis({
        method: 'PUT',
        url: `/org-people/departments/${departmentId}/members/${memberId}`,
        data: { role: 'lead' },
      });
      await loadDepartment();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Lead updated successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (err) {
      console.error('Error setting lead:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to update lead'),
        modalType: 'error',
        duration: 3000,
      }));
    }
  };


  const handlePostFeedEvent = async (eventData: { event_type: string; message: string; metadata?: any }) => {
    try {
      await dispatch(postDepartmentFeedEvent(departmentId, eventData));
      await loadFeed();
    } catch (err) {
      console.error('Error posting feed event:', err);
    }
  };

  const handleUploadDocument = async (files: File[]) => {
    setIsUploadingDocs(true);
    setUploadingDocCount(files.length);
    dispatch(hideModal());
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }

      const response = await apis.post(
        `/org-people/departments/${departmentId}/documents/bulk-upload`,
        formData
      );

      if (response?.data?.success) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.message || t('Documents uploaded successfully'),
          modalType: 'success',
          duration: 3000,
        }));
      }

      await loadDocuments();
      await loadDepartment();
      await loadFeed();
    } catch (err) {
      console.error('Error uploading document:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsUploadingDocs(false);
      setUploadingDocCount(0);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await apis.delete(`/org-people/departments/${departmentId}/documents/${documentId}`);
      await loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `Delete failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        modalType: 'error',
        duration: 3000,
      }));
    }
  };

  const handleBulkDeleteDocuments = async (documentIds: string[]) => {
    await apis.post(`/org-people/departments/${departmentId}/documents/bulk-delete`, { document_ids: documentIds });
    await loadDocuments();
    await loadDepartment();
  };

  const handleAddComment = async (eventId: string, comment: string) => {
    try {
      if (!department?.id) {
        throw new Error('Department ID not available');
      }
      const displayedIndex = parseInt(eventId, 10);
      const originalIndex = feed.length - 1 - displayedIndex;
      const url = `/org-people/departments/${department.id}/feed/${originalIndex}/comments`;

      const response = await apis.post(url, { message: comment });
      await loadFeed();
    } catch (err) {
      console.error('Error adding comment:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Full error:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `Comment failed: ${errorMessage}`,
        modalType: 'error',
        duration: 5000,
      }));
    }
  };

  const handleDeleteDepartment = () => {
    dispatch(showModal({
      type: 'confirmation',
      message: t('Delete Department'),
      subMsg: t('This department and all associated data will be permanently deleted. This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-red-500' />,
      isDest: true,
      showModal: true,
      onConfirm: async () => {
        if (!department?.id) return;
        const result = await dispatch(deleteDepartment(department.id) as any);
        dispatch(hideModal());
        if (result) {
          history.push('/departments');
        }
      },
    }));
  };



  const handleOpenDocumentUploadModal = () => {
    dispatch(showModal({
      type: 'uploadAttachment',
      message: t('Upload Department Document'),
      icon: <PiPlusCircleLight className='w-6 h-6' />,
      showModal: true,
      onConfirm: handleUploadDocument,
    }));
  };

  // Render member avatars for the header
  const memberAvatars = department?.members && department.members.length > 0 ? (
    <div className="flex items-center gap-3">
      <div className="flex -space-x-1.5">
        {department.members.slice(0, 4).map((member, idx) => (
          <div
            key={member.id}
            className="relative hover:z-20 transition-all"
            style={{ zIndex: 50 - idx }}
          >
            <ProfileImage
              profilePicture={member.profile_picture || null}
              email={member.email}
              initials={`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase()}
              size="w-6 h-6"
              firstName={member.first_name}
              lastName={member.last_name}
              className="ring-1.5 ring-white dark:ring-gray-900"
            />
          </div>
        ))}
        {department.members.length > 4 && (
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 ring-1.5 ring-white dark:ring-gray-900 text-xs font-semibold text-gray-600 dark:text-gray-300">
            +{department.members.length - 4}
          </div>
        )}
      </div>
    </div>
  ) : null;

  const tabs = [
    { label: t('Details'), value: 'Details' },
    { label: t('Feed'), value: 'feed' },
    { label: t('Matters'), value: 'matters', disabled: true, tooltip: t('Coming soon') },
    { label: t('Documents'), value: 'documents' },
  ];

  if (loading) {
    return (
      <DetailFrame
        breadcrumbs={[]}
        dropdownActions={[]}
        subscriptionActive={true}
        showBackButton={true}
      >
        <DetailPageSkeleton />
      </DetailFrame>
    );
  }

  if (error || !department) {
    return (
      <DetailFrame
        breadcrumbs={[]}
        dropdownActions={[]}
        subscriptionActive={true}
        showBackButton={true}
      >
        <div className="text-center py-12">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || t('Department not found')}</p>
          <Button onClick={() => history.push('/departments')}>
            <PiArrowLeftLight className="w-4 h-4 mr-2" />
            {t('Back to Departments')}
          </Button>
        </div>
      </DetailFrame>
    );
  }

  return (
    <DetailFrame
      breadcrumbs={[
        { name: t('Departments'), href: '/departments', current: false },
        { name: department.name, href: `/departments/${departmentId}`, current: true },
      ]}
      dropdownActions={[
        {
          label: t('Activity log'),
          icon: <PiClockCounterClockwiseLight className='w-4 h-4' />,
          onClick: () => setShowAuditLog(prev => !prev),
        },
        {
          label: t('Delete'),
          icon: <PiTrashLight className='w-4 h-4' />,
          onClick: handleDeleteDepartment,
        },
      ]}
      subscriptionActive={true}
      showBackButton={true}
      additionalHeaderElements={memberAvatars}
    >
      <div className="flex flex-col h-full w-full">

        {/* Tabs - Static */}
        <div data-tour-id="dept-detail-tabs" className="flex-shrink-0 bg-background-light dark:bg-background-dark border-b border-gray-200 dark:border-gray-700 mt-2">
          <div className="flex items-center justify-between">
            <FilterBarWithTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabClick={(val) => setActiveTab(val as any)}
              noParentHeight
              noParentPadding={true}
            />
            {activeTab === 'documents' && (
              <Button
                size="small"
                onClick={handleOpenDocumentUploadModal}
                className="ml-auto mr-2"
              >
                <PiPlusCircleLight className="w-4 h-4 mr-1" />
                {t('Add Document')}
              </Button>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <motion.div
          data-tour-id="dept-detail-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`flex-1 flex flex-col min-h-0 mt-2 ${activeTab !== 'feed' ? 'overflow-y-auto custom-scrollbar' : ''}`}
        >
          {activeTab === 'Details' && (
            <div className="max-h-[calc(100vh-100px)] overflow-y-auto custom-scrollbar-main px-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                {/* ── Left Column ── */}
                <div className="lg:col-span-2 space-y-5">

                  {/* ── Description Card ── */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <PiTextAlignLeftLight className="h-4 w-4" />
                        <span className="text-xs font-medium">{t('Description')}</span>
                      </div>
                      {!isEditingDeptInfo && (
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => setIsEditingDeptInfo(true)}
                          icon={<PiPencilLight className="h-3.5 w-3.5" />}
                        >
                          {t('Edit')}
                        </Button>
                      )}
                    </div>

                    {isEditingDeptInfo ? (
                      <div className="p-5 space-y-4">
                        <Input
                          type="text"
                          label={t('Department Name')}
                          placeholder={t('Enter department name')}
                          value={editedDeptFields.name || ''}
                          onChange={(e) => handleDeptFieldChange('name', e.target.value)}
                          required
                          className="w-full"
                        />
                        <Textarea
                          label={t('Description')}
                          placeholder={t('Enter department description')}
                          value={editedDeptFields.description || ''}
                          onChange={(e) => handleDeptFieldChange('description', e.target.value)}
                          className="w-full"
                          id="dept-description"
                        />
                        <Dropdown
                          label={t('Practice Areas')}
                          options={practiceAreaOptions}
                          value={(editedDeptFields.practice_area_ids || []).map(String)}
                          onChange={(vals) => handleDeptFieldChange('practice_area_ids', Array.isArray(vals) ? vals.map(Number) : [Number(vals)])}
                          multiple
                          fullWidth
                          placeholder={t('Select practice areas')}
                        />

                        <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                          <Button
                            variant="neutral"
                            size="small"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                          >
                            {t('Cancel')}
                          </Button>
                          <Button
                            variant="primary"
                            size="small"
                            onClick={handleSaveDepartment}
                            disabled={isSaving}
                            icon={isSaving ? <PiSpinnerLight className="h-4 w-4 animate-spin" /> : undefined}
                          >
                            {isSaving ? t('Saving...') : t('Save Changes')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-5">
                        {department.description ? (
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-w-prose">
                            {department.description}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                            {t('No description provided')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Metadata Grid ── */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

                    {/* Status */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <PiCircleDashedLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Status')}</span>
                      </div>
                      <Badge color={department.is_active ? 'green' : 'gray'}>
                        {department.is_active ? t('Active') : t('Inactive')}
                      </Badge>
                    </div>

                    {/* Members */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <PiUsersLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Members')}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {department.members?.length || 0}
                      </p>
                    </div>

                    {/* Documents */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <PiTagLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Documents')}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {(department as any).document_count || 0}
                      </p>
                    </div>

                    {/* Created */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
                      <div className="flex items-center gap-1.5 mb-2">
                        <PiCalendarBlankLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created')}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {department.created_at ? formatDate(department.created_at) : '-'}
                      </p>
                    </div>
                  </div>

                  {/* ── Practice Areas Card ── */}
                  {department.practice_areas && department.practice_areas.length > 0 && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                        <PiTagLight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-400 dark:text-gray-500">{t('Practice Areas')}</span>
                      </div>
                      <div className="p-4">
                        <div className="flex flex-wrap gap-2">
                          {department.practice_areas.map((pa) => (
                            <Badge key={pa.id} color="blue">
                              {pa.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Activity Row ── */}
                  {department.created_at && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
                        <PiCalendarBlankLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Created')}</span>
                          <p className="text-sm text-gray-700 dark:text-gray-200">{formatDate(department.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Recent Activity ── */}
                  <RecentActivityCard
                    fetchEntries={async (limit) => {
                      const res: any = await dispatch(fetchEntityAuditLog('Department', departmentId, 1, limit) as any);
                      return res?.data || [];
                    }}
                  />
                </div>

                {/* ── Right Column ── */}
                <div className="space-y-5">

                  {/* ── Members Card (read-only list + Manage button) ── */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <PiUsersLight className="h-4 w-4" />
                        <span className="text-xs font-medium">
                          {t('Members')} ({(department.members || []).length})
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => dispatch(showSidePanel({
                          type: 'manageResourceCollaborators',
                          showSidePanel: true,
                          width: '50%',
                          message: t('Manage Members'),
                          subMsg: t('Add or remove members of this department.'),
                          icon: <PiUsersLight className="w-5 h-5" />,
                          resourceUuid: departmentId,
                          resourceType: 'department',
                          collaborators: (department.members || []).map((m: any) => ({
                            user_id: m.id,
                            collaborator_uuid: m.uuid || String(m.id),
                            name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || '',
                            email: m.email || '',
                            initials: m.initials || ((m.first_name?.[0] || '') + (m.last_name?.[0] || '')).toUpperCase() || '??',
                            profile_picture: m.profile_picture || null,
                            access: m.role === 'lead' ? 'admin' : (m.role === 'member' ? 'edit' : 'view'),
                            role: m.role,
                            source: 'direct',
                          })),
                          currentUserUuid: userInfo?.UserID ? String(userInfo.UserID) : undefined,
                          canAdmin: true,
                          addCollaborator: async (uuid: string, userUuid: string, _access: string) => {
                            await dispatch(addDepartmentMember(uuid, userUuid, 'member') as any);
                          },
                          removeCollaborator: async (uuid: string, collabUuid: string) => {
                            await dispatch(removeDepartmentMember(uuid, collabUuid) as any);
                          },
                          updateAccess: async (_uuid: string, _collabUuid: string, _access: string) => {
                            return true;
                          },
                          onUpdate: () => { loadDepartment(); },
                        } as any))}
                      >
                        {t('Manage')}
                      </Button>
                    </div>
                    <div className="p-4">
                      {(department.members || []).length === 0 ? (
                        <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                          {t('No members yet.')}
                        </p>
                      ) : (
                        <ul className="space-y-2.5">
                          {(department.members || []).slice(0, 8).map((m: any, idx: number) => {
                            const name = m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || '';
                            const initials = m.initials || ((m.first_name?.[0] || '') + (m.last_name?.[0] || '')).toUpperCase() || '??';
                            return (
                              <li key={m.id || idx} className="flex items-center gap-2.5">
                                <ProfileImage
                                  profilePicture={m.profile_picture || null}
                                  email={m.email || ''}
                                  initials={initials}
                                  firstName={m.first_name}
                                  lastName={m.last_name}
                                  size="w-7 h-7"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{name}</p>
                                  {m.email && <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{m.email}</p>}
                                </div>
                                {m.role === 'lead' ? (
                                  <Badge color="blue">{t('Lead')}</Badge>
                                ) : (
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500">{t('Member')}</span>
                                )}
                              </li>
                            );
                          })}
                          {(department.members || []).length > 8 && (
                            <li className="text-[11px] text-gray-400 text-center pt-1">
                              +{(department.members || []).length - 8} {t('more')}
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'feed' && (
            <div className="flex-1 min-h-0 flex flex-col">
              <FeedComponent
                events={feed}
                loading={feedLoading}
                onRefresh={loadFeed}
                onPostEvent={handlePostFeedEvent}
                onCommentAdd={handleAddComment}
                currentUser={
                  userInfo
                    ? {
                        id: userInfo.UserID || 'current-user',
                        first_name: userInfo.FirstName || 'You',
                        last_name: userInfo.LastName || '',
                        email: userInfo.Email || '',
                      }
                    : undefined
                }
                teamMembers={department?.members || []}
              />
            </div>
          )}

          {activeTab === 'matters' && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {t('Matters for this department will appear here')}
            </div>
          )}

          {activeTab === 'documents' && (
            <DocumentsTab
              departmentId={departmentId}
              documents={documents}
              loading={documentsLoading}
              isUploading={isUploadingDocs}
              uploadingCount={uploadingDocCount}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onBulkDelete={handleBulkDeleteDocuments}
              onRefresh={loadDocuments}
            />
          )}

        </motion.div>
      </div>

      <FeatureTour
        steps={departmentDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />

      {/* Audit Log Side Panel with Backdrop */}
      {showAuditLog && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300"
            onClick={() => setShowAuditLog(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
            <AuditLogPanel
              entityType="Department"
              entityId={departmentId}
              entityName={department?.name}
              onClose={() => setShowAuditLog(false)}
            />
          </div>
        </>
      )}
    </DetailFrame>
  );
};

export default DepartmentDetail;
